/**
 * Статусы: свой (выбранный в меню, свой статус текстом, «отошёл» по бездействию) и чужие
 * активности — свой статус под именем и чем человек занят. Сами статусы других («в сети»,
 * «не беспокоить») лежат в сторе друзей рядом с играми; здесь — то, что для них новое.
 */
import { create } from 'zustand'
import { customStatusOf, settingsApi } from '@/lib/presence'
import { useAppStore } from '@/store/app'
import { useSocialStore } from '@/store/social'
import type { PresenceActivity, PresenceStatus, PresenceUpdate, SelfStatus, UserSettings } from '@/types/api'

const SELF: SelfStatus[] = ['online', 'idle', 'dnd', 'invisible']

interface PresenceState {
  /** Выбранный статус: хранится на сервере, приходит в READY и USER_SETTINGS_UPDATE. */
  status: SelfStatus
  /** Свой статус текстом; пусто — не задан. */
  customText: string
  /** Когда свой статус уберётся сам (RFC 3339); null — не уберётся. */
  customUntil: string | null
  /** Показывать ли друзьям и серверам запущенную игру: настройка учётной записи, не компьютера. */
  showGame: boolean
  /** Клиент решил, что человек отошёл: 10 минут без клавиатуры и мыши. */
  autoIdle: boolean
  /** Активности других из присутствия (id → список), только у тех, кто в сети. */
  activities: Record<string, PresenceActivity[]>

  applySettings: (s: Partial<Pick<UserSettings, 'custom_status' | 'show_current_game'>> & { status?: string }) => void
  ingest: (list: PresenceUpdate[]) => void
  setAutoIdle: (idle: boolean) => void
  /** Выбрать статус: сразу в интерфейсе, откатывается, если сервер не принял. */
  setStatus: (status: SelfStatus) => Promise<void>
  /** Свой статус текстом и срок, когда он уберётся сам (null — не уберётся); пустая строка убирает его. */
  setCustomStatus: (text: string, until: Date | null) => Promise<void>
  /** Показывать ли игру: сразу в интерфейсе, откатывается, если сервер не принял. */
  setShowGame: (show: boolean) => Promise<void>
  reset: () => void
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  status: 'online',
  customText: '',
  customUntil: null,
  showGame: true,
  autoIdle: false,
  activities: {},

  applySettings: (s) =>
    set((st) => ({
      status: s.status && (SELF as string[]).includes(s.status) ? (s.status as SelfStatus) : st.status,
      customText: s.custom_status === undefined ? st.customText : (s.custom_status?.text ?? ''),
      customUntil: s.custom_status === undefined ? st.customUntil : (s.custom_status?.expires_at ?? null),
      showGame: typeof s.show_current_game === 'boolean' ? s.show_current_game : st.showGame,
    })),

  // список участников присылает всех видимых при каждом пересчёте — новый объект только если что-то поменялось,
  // иначе каждый пересчёт перерисовывал бы всех подписчиков
  ingest: (list) =>
    set((s) => {
      let activities: Record<string, PresenceActivity[]> | null = null
      for (const p of list) {
        const next = p.status !== 'offline' && p.activities?.length ? p.activities : undefined
        const prev = (activities ?? s.activities)[p.user.id]
        if (prev === next || (prev && next && JSON.stringify(prev) === JSON.stringify(next))) continue
        activities ??= { ...s.activities }
        if (next) activities[p.user.id] = next
        else delete activities[p.user.id]
      }
      return activities ? { activities } : {}
    }),

  setAutoIdle: (autoIdle) => set((s) => (s.autoIdle === autoIdle ? {} : { autoIdle })),

  setStatus: async (status) => {
    const prev = get().status
    set({ status })
    try {
      get().applySettings(await settingsApi.update({ status }))
    } catch (err) {
      set({ status: prev })
      throw err
    }
  },

  setCustomStatus: async (text, until) => {
    const clean = text.trim()
    const next = await settingsApi.update({ custom_status: clean ? { text: clean, expires_at: until ? until.toISOString() : null } : null })
    get().applySettings(next)
  },

  setShowGame: async (show) => {
    const prev = get().showGame
    set({ showGame: show })
    try {
      get().applySettings(await settingsApi.update({ show_current_game: show }))
    } catch (err) {
      set({ showGame: prev })
      throw err
    }
  },

  reset: () => set({ status: 'online', customText: '', customUntil: null, showGame: true, autoIdle: false, activities: {} }),
}))

/** Свой статус, каким его видят остальные: «в сети» после 10 минут без ввода — «неактивен». */
export function useSelfStatus(): SelfStatus {
  return usePresenceStore((s) => (s.status === 'online' && s.autoIdle ? 'idle' : s.status))
}

/** Статус любого пользователя; свой — из меню статуса, а не из событий. */
export function useStatusOf(userId: string | undefined): PresenceStatus {
  const me = useAppStore((s) => s.currentUser?.id)
  const self = useSelfStatus()
  const other = useSocialStore((s) => (userId ? s.presences[userId] : undefined))
  if (userId && userId === me) return self
  return other ?? 'offline'
}

/** Свой статус текстом под именем; пусто — не задан или человек не в сети. */
export function useCustomStatus(userId: string | undefined): string {
  const me = useAppStore((s) => s.currentUser?.id)
  return usePresenceStore((s) => (!userId ? '' : userId === me ? s.customText : customStatusOf(s.activities[userId])))
}
